import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { Lan as LanIcon } from '@mui/icons-material';
import WidgetCard from '../WidgetCard';
import { NetworkSpreadsheetService } from '../../../services/NetworkSpreadsheetService';
import { SpreadsheetDocumentsData } from '../../../models/SpreadsheetDocument';

/**
 * 개요의 IP 리스트 위젯.
 *
 * 데이터 소스는 현장 설정의 "IP 네트워크 스프레드시트"(projects.network_spreadsheet)
 * 와 동일하다 — "관리" 액션이 그 섹션으로 이동하므로 위젯도 같은 소스를 요약해야
 * 한다. (이전엔 별도 site_network_devices 테이블을 읽어 스프레드시트에 입력해도
 * 개요에 0건으로 보이는 불일치가 있었다.)
 *
 * 개요는 **문서(그룹) 이름 + 등록 행 수**만 보여준다. 행 단위로 나열하면 현장 규모에
 * 따라 수백 줄이 되어 카드 밖으로 넘친다 — 개별 IP 는 "관리"로 들어가서 본다.
 */

interface IpListWidgetProps {
  projectId: number;
  /** "관리" 액션 클릭 시 호출 — 부모(OverviewSection)가 IP/네트워크 섹션으로 전환. */
  onActionClick?: () => void;
}

const IpListWidget: React.FC<IpListWidgetProps> = ({ projectId, onActionClick }) => {
  const [data, setData] = useState<SpreadsheetDocumentsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    NetworkSpreadsheetService.get(projectId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const summary = useMemo(() => {
    if (!data) return null;
    // 문서(그룹)별 비어있지 않은 행 수만 집계한다. 문서 수는 현장당 한 자릿수라
    // 카드를 넘칠 일이 없다.
    const groups = data.documents
      .map((doc) => ({
        id: doc.id,
        name: doc.name?.trim() || '(이름 없음)',
        count: doc.rows.filter((r) =>
          Object.values(r.cells).some((v) => v && v.trim() !== ''),
        ).length,
      }))
      .filter((g) => g.count > 0);
    return { total: groups.reduce((n, g) => n + g.count, 0), groups };
  }, [data]);

  return (
    <WidgetCard
      title="IP 리스트"
      icon={<LanIcon fontSize="small" />}
      actionLabel="관리"
      onAction={onActionClick}
      testId="widget-ip"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
          {error}
        </Alert>
      ) : !summary || summary.total === 0 ? (
        <Typography variant="body2" color="text.secondary">
          등록된 장비가 없습니다.
        </Typography>
      ) : (
        <>
          {summary.groups.map((g) => (
            <Box
              key={g.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 1,
                py: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {g.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {g.count}행
              </Typography>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            전체 {summary.total}행
          </Typography>
        </>
      )}
    </WidgetCard>
  );
};

export default IpListWidget;
